import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import { addOrUpdateBooking } from "../../../../../../APIs/Booking/UpdateBooking";
import { updateBooking } from "../SelectedBookingManageController";
import { EDSQuantityStepper } from "../../../../../eds/EDSQuantityStepper";
import { Input } from "../../../../../ui/input";
import { getCurrencySymbol } from "../../../../../../utils/number";

const BookingActuals = () => {
  const diaryContext = useDiaryContext();
  const {
    selectedBooking,
    selectedBookingV4,
    bookings,
    setDiaryContext,
    venueConfig
  } = diaryContext;
  const { venueGroupConfig } = useAppContext();
  const notificationsContext = useNotificationsContext();
  const diaryContextRef = useRef(diaryContext);
  diaryContextRef.current = diaryContext;

  const guestsDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const previousGuestsRef = useRef<number>(0);

  const isCollinsJuice = venueGroupConfig?.product_tier === "juice";
  const isWalkIn = selectedBookingV4?.walk_in ?? selectedBooking?.walkIn;
  const arrived = (selectedBooking?.currentStage ?? "") !== "";
  const currencySymbol = getCurrencySymbol(venueConfig?.currency ?? "GBP");
  const currencyCode = venueConfig?.currency ?? "GBP";

  const bookingDate = selectedBookingV4?.date ?? selectedBooking?.date;
  const isTodayOrBefore = useMemo(() => {
    if (!bookingDate) {
      return false;
    }
    const date = new Date(bookingDate);
    date.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return date <= now;
  }, [bookingDate]);

  const shouldShow = arrived && isTodayOrBefore && !isCollinsJuice;

  const [actualGuests, setActualGuests] = useState<number>(
    selectedBooking?.actualGuests ?? 0
  );
  const [actualSpendInput, setActualSpendInput] = useState<string>(
    selectedBooking?.actualSpend?.toString() ?? ""
  );

  useEffect(() => {
    setActualGuests(selectedBooking?.actualGuests ?? 0);
    setActualSpendInput(selectedBooking?.actualSpend?.toString() ?? "");
  }, [selectedBooking?.actualGuests, selectedBooking?.actualSpend]);

  useEffect(() => {
    return () => {
      if (guestsDebounceTimer.current) {
        clearTimeout(guestsDebounceTimer.current);
      }
    };
  }, []);

  const handleActualGuestsChange = useCallback(
    (newValue: number) => {
      if (!selectedBooking || !bookings) {
        return;
      }

      if (!guestsDebounceTimer.current) {
        previousGuestsRef.current = selectedBooking.actualGuests ?? 0;
      }

      setActualGuests(newValue);

      const { updatedBookings, updatedBooking } = updateBooking({
        selectedBooking,
        bookings,
        bookingPropsToSave: {
          actualGuests: newValue
        }
      });

      setDiaryContext({ bookings: updatedBookings });

      if (guestsDebounceTimer.current) {
        clearTimeout(guestsDebounceTimer.current);
      }

      guestsDebounceTimer.current = setTimeout(async () => {
        guestsDebounceTimer.current = null;
        try {
          const response = await addOrUpdateBooking({
            booking: updatedBooking,
            diaryContext: diaryContextRef.current
          });

          if (response?.unsaved) {
            setActualGuests(previousGuestsRef.current);
            setDiaryContext({ bookings });
            notificationsContext.addNotification({
              heading: "Failed to update actual guests",
              autoClose: 4000,
              toastType: "error"
            });
          }
        } catch {
          setActualGuests(previousGuestsRef.current);
          setDiaryContext({ bookings });
          notificationsContext.addNotification({
            heading: "Failed to update actual guests",
            autoClose: 4000,
            toastType: "error"
          });
        }
      }, 300);
    },
    [selectedBooking, bookings, setDiaryContext, notificationsContext]
  );

  const handleActualSpendBlur = useCallback(async () => {
    if (!selectedBooking || !bookings) {
      return;
    }

    const parsedValue = parseFloat(actualSpendInput);

    if (!isNaN(parsedValue) && parsedValue < 0) {
      setActualSpendInput(selectedBooking.actualSpend?.toString() ?? "");
      return;
    }

    const newValue = isNaN(parsedValue) ? undefined : parsedValue;

    if (newValue === selectedBooking.actualSpend) {
      return;
    }

    const { updatedBookings, updatedBooking } = updateBooking({
      selectedBooking,
      bookings,
      bookingPropsToSave: {
        actualSpend: newValue
      }
    });

    setDiaryContext({ bookings: updatedBookings });

    try {
      const response = await addOrUpdateBooking({
        booking: updatedBooking,
        diaryContext: diaryContextRef.current
      });

      if (response?.unsaved) {
        setActualSpendInput(selectedBooking.actualSpend?.toString() ?? "");
        setDiaryContext({ bookings });
        notificationsContext.addNotification({
          heading: "Failed to update actual spend",
          autoClose: 4000,
          toastType: "error"
        });
      }
    } catch {
      setActualSpendInput(selectedBooking.actualSpend?.toString() ?? "");
      setDiaryContext({ bookings });
      notificationsContext.addNotification({
        heading: "Failed to update actual spend",
        autoClose: 4000,
        toastType: "error"
      });
    }
  }, [
    selectedBooking,
    bookings,
    actualSpendInput,
    setDiaryContext,
    notificationsContext
  ]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="BookingActuals pt-[var(--panelPadding)] border-t border-border-color mt-[var(--panelPadding)]">
      <div className="BookingActuals-Title font-bold text-[length:var(--fontSize-base)]">
        Booking Actuals
      </div>
      <div className="BookingActuals-Content mt-[var(--panelPadding)] flex flex-col items-start gap-[var(--panelPadding)]">
        {!isWalkIn && (
          <div className="flex flex-col gap-spacing min-w-0">
            <label
              className="text-sm font-medium text-foreground"
              htmlFor="actual-guests-input"
            >
              Actual guests
            </label>
            <EDSQuantityStepper
              inputId="actual-guests-input"
              value={actualGuests}
              onChange={handleActualGuestsChange}
              min={0}
              max={999}
              step={1}
              size="md"
            />
          </div>
        )}
        <div className="flex flex-col gap-spacing min-w-0">
          <label
            className="text-sm font-medium text-foreground"
            htmlFor="actual-spend-input"
          >
            Actual spend
          </label>
          <div className="flex items-center gap-0 relative">
            <span
              className="absolute left-[0.625em] text-sm text-muted-foreground pointer-events-none z-[1]"
              aria-hidden="true"
            >
              {currencySymbol}
            </span>
            <Input
              id="actual-spend-input"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={actualSpendInput}
              onChange={(e) => setActualSpendInput(e.target.value)}
              onBlur={handleActualSpendBlur}
              className="h-9 text-sm w-[8rem] pl-[1.75em] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0"
              aria-label={`Actual spend in ${currencyCode}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingActuals;
